import axios, { InternalAxiosRequestConfig } from 'axios';
import { IBestellung, IBestellungMitPositionen, IBestellposition, IArtikel, IKreditor, IMengeneinheit } from '../../shared/models';
import { IApprovalFlow } from '../../shared/models/IApprovalFlow';
import { IBestellungKommentar, IBestellpositionKommentar } from '../../shared/models/IKommentar';

const API_BASE_URL = process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3000/api';

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Session-Cookies mitsenden
});

// Bestellungen API
export const bestellungenApi = {
  getAll: async (): Promise<IBestellungMitPositionen[]> => {
    const response = await apiClient.get('/bestellungen');
    return response.data;
  },

  getById: async (id: number): Promise<IBestellungMitPositionen> => {
    const response = await apiClient.get(`/bestellungen/${id}`);
    return response.data;
  },

  getAllForTemplates: async (): Promise<any[]> => {
    const response = await apiClient.get('/bestellungen/all-for-templates');
    return response.data;
  },

  create: async (bestellung: Partial<IBestellung>): Promise<IBestellung> => {
    const response = await apiClient.post('/bestellungen', bestellung);
    return response.data;
  },

  update: async (id: number, updates: Partial<IBestellung>): Promise<IBestellung> => {
    const response = await apiClient.patch(`/bestellungen/${id}`, updates);
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await apiClient.delete(`/bestellungen/${id}`);
  },

  getPositionen: async (bestellungId: number): Promise<IBestellposition[]> => {
    const response = await apiClient.get(`/bestellungen/${bestellungId}/positionen`);
    return response.data;
  },

  createPosition: async (bestellungId: number, position: Partial<IBestellposition>, autoApprove?: boolean): Promise<IBestellposition> => {
    const response = await apiClient.post(`/bestellungen/${bestellungId}/positionen`, { ...position, _autoApprove: autoApprove });
    return response.data;
  },

  updatePosition: async (bestellungId: number, positionId: number, updates: Partial<IBestellposition>, autoApprove?: boolean): Promise<IBestellposition> => {
    const response = await apiClient.patch(`/bestellungen/${bestellungId}/positionen/${positionId}`, { ...updates, _autoApprove: autoApprove });
    return response.data;
  },

  deletePosition: async (bestellungId: number, positionId: number): Promise<void> => {
    await apiClient.delete(`/bestellungen/${bestellungId}/positionen/${positionId}`);
  },
};

// Artikel API
export const artikelApi = {
  getAll: async (): Promise<IArtikel[]> => {
    const response = await apiClient.get('/artikel');
    return response.data;
  },

  getById: async (id: number): Promise<IArtikel> => {
    const response = await apiClient.get(`/artikel/${id}`);
    return response.data;
  },

  create: async (artikel: Partial<IArtikel>): Promise<IArtikel> => {
    const response = await apiClient.post('/artikel', artikel);
    return response.data;
  },

  update: async (id: number, updates: Partial<IArtikel>): Promise<IArtikel> => {
    const response = await apiClient.patch(`/artikel/${id}`, updates);
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await apiClient.delete(`/artikel/${id}`);
  },
};

// Kreditoren API
export const kreditorenApi = {
  getAll: async (): Promise<IKreditor[]> => {
    const response = await apiClient.get('/kreditoren');
    return response.data;
  },

  getById: async (id: number): Promise<IKreditor> => {
    const response = await apiClient.get(`/kreditoren/${id}`);
    return response.data;
  },

  create: async (kreditor: Partial<IKreditor>): Promise<IKreditor> => {
    const response = await apiClient.post('/kreditoren', kreditor);
    return response.data;
  },

  update: async (id: number, updates: Partial<IKreditor>): Promise<IKreditor> => {
    const response = await apiClient.patch(`/kreditoren/${id}`, updates);
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await apiClient.delete(`/kreditoren/${id}`);
  },
};

// Mengeneinheiten API
export const lagerorteApi = {
  getAll: async (): Promise<{ Code: string; Name: string | null }[]> => {
    const response = await apiClient.get('/lagerorte');
    return response.data;
  },
};

export const mengeneinheitenApi = {
  getAll: async (): Promise<IMengeneinheit[]> => {
    const response = await apiClient.get('/mengeneinheiten');
    return response.data;
  },

  create: async (mengeneinheit: Partial<IMengeneinheit>): Promise<IMengeneinheit> => {
    const response = await apiClient.post('/mengeneinheiten', mengeneinheit);
    return response.data;
  },

  update: async (id: number, updates: Partial<IMengeneinheit>): Promise<IMengeneinheit> => {
    const response = await apiClient.patch(`/mengeneinheiten/${id}`, updates);
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await apiClient.delete(`/mengeneinheiten/${id}`);
  },
};

// Approval Flow API
export const approvalFlowApi = {
  // Alle Flows laden (mit erweiterten Informationen)
  getAllFlows: async (): Promise<any[]> => {
    const response = await apiClient.get('/approval/flows');
    return response.data;
  },

  // Flows für eine Bestellung laden
  getFlowsForBestellung: async (bestellungId: number): Promise<IApprovalFlow[]> => {
    const response = await apiClient.get(`/approval/flows?bestellungId=${bestellungId}`);
    return response.data;
  },

  // Einzelnen Flow laden
  getFlowById: async (flowId: number): Promise<IApprovalFlow> => {
    const response = await apiClient.get(`/approval/flows/${flowId}`);
    return response.data;
  },

  // Flows für alle Positionen einer Bestellung erstellen
  // WICHTIG: Erstellt NUR Position-Flows, KEINE Bestellung-Flows!
  createFlowForBestellung: async (bestellungId: number, departmentName: string, creatorEmail?: string): Promise<IApprovalFlow[]> => {
    const response = await apiClient.post('/approval/flows/bestellung', {
      bestellungId,
      departmentName,
      creatorEmail,
    });
    return response.data;
  },

  // Flow für Position erstellen
  createFlowForPosition: async (
    positionId: number,
    bestellungId: number,
    departmentName: string,
    creatorEmail?: string
  ): Promise<IApprovalFlow> => {
    const response = await apiClient.post('/approval/flows/position', {
      positionId,
      bestellungId,
      departmentName,
      creatorEmail,
    });
    return response.data;
  },

  // Schritt genehmigen
  approveStep: async (flowId: number, userEmail: string, comments?: string, stepType?: 'Department' | 'Management'): Promise<IApprovalFlow> => {
    const response = await apiClient.post(`/approval/flows/${flowId}/approve`, {
      userEmail,
      comments,
      stepType,
    });
    return response.data;
  },

  // Schritt ablehnen
  rejectStep: async (flowId: number, userEmail: string, comments?: string, stepType?: 'Department' | 'Management'): Promise<IApprovalFlow> => {
    const response = await apiClient.post(`/approval/flows/${flowId}/reject`, {
      userEmail,
      comments,
      stepType,
    });
    return response.data;
  },

  // Aggregierten Genehmigungsstatus einer Bestellung aus Position-Flows abrufen
  getBestellungAggregatedStatus: async (bestellungId: number): Promise<IApprovalFlow['OverallStatus']> => {
    const response = await apiClient.get(`/approval/flows/bestellung-status/${bestellungId}`);
    return response.data.status;
  },

  // Flow löschen (für Admin/Einkauf zum Ändern des Departments)
  deleteFlow: async (flowId: number): Promise<void> => {
    await apiClient.delete(`/approval/flows/${flowId}`);
  },

  // Direktfreigabe für eine Position erstellen (nur Admin/Einkauf)
  createDirektfreigabe: async (positionId: number, bestellungId: number): Promise<IApprovalFlow> => {
    const response = await apiClient.post('/approval/flows/direktfreigabe', { positionId, bestellungId });
    return response.data;
  },
};

// Kommentar API
export const kommentareApi = {
  // Kommentare für Bestellung laden
  getBestellungKommentare: async (bestellungId: number): Promise<IBestellungKommentar[]> => {
    const response = await apiClient.get(`/kommentare/bestellung/${bestellungId}`);
    return response.data;
  },

  // Kommentare für Position laden
  getPositionKommentare: async (positionId: number): Promise<IBestellpositionKommentar[]> => {
    const response = await apiClient.get(`/kommentare/position/${positionId}`);
    return response.data;
  },

  // ALLE Kommentare für eine Bestellung (Bestellung- UND Position-Kommentare)
  getAlleKommentare: async (bestellungId: number): Promise<{
    bestellungKommentare: IBestellungKommentar[];
    positionKommentare: IBestellpositionKommentar[];
  }> => {
    const response = await apiClient.get(`/kommentare/bestellung/${bestellungId}/alle`);
    return response.data;
  },

  // Kommentar für Bestellung erstellen
  createBestellungKommentar: async (
    bestellungId: number,
    kommentartext: string,
    authorEmail: string,
    erwaehntPersonen?: string[],
    parentKommentarId?: string
  ): Promise<IBestellungKommentar> => {
    const response = await apiClient.post('/kommentare/bestellung', {
      BestellungID: bestellungId.toString(),
      Kommentartext: kommentartext,
      AuthorEmail: authorEmail,
      ErwaehntPersonen: erwaehntPersonen,
      ParentKommentarID: parentKommentarId,
    });
    return response.data;
  },

  // Kommentar für Position erstellen
  createPositionKommentar: async (
    bestellungId: number,
    positionId: number,
    posID: number,
    kommentartext: string,
    authorEmail: string,
    erwaehntPersonen?: string[],
    parentKommentarId?: string
  ): Promise<IBestellpositionKommentar> => {
    const response = await apiClient.post('/kommentare/position', {
      BestellungID: bestellungId.toString(),
      BestellpositionID: positionId.toString(),
      PosID: posID,
      Kommentartext: kommentartext,
      AuthorEmail: authorEmail,
      ErwaehntPersonen: erwaehntPersonen,
      ParentKommentarID: parentKommentarId,
    });
    return response.data;
  },

  // Kommentar aktualisieren
  updateKommentar: async (
    kommentarId: number,
    kommentartext?: string,
    erwaehntPersonen?: string[]
  ): Promise<IBestellungKommentar | IBestellpositionKommentar> => {
    const response = await apiClient.patch(`/kommentare/${kommentarId}`, {
      Kommentartext: kommentartext,
      ErwaehntPersonen: erwaehntPersonen,
    });
    return response.data;
  },

  // Kommentar löschen (typ: 'bestellung' oder 'position' für die richtige Liste)
  deleteKommentar: async (kommentarId: number, typ: 'bestellung' | 'position'): Promise<void> => {
    await apiClient.delete(`/kommentare/${typ}/${kommentarId}`);
  },
};

// Approval Matrix API
export const approvalMatrixApi = {
  // Alle Freigabe-Matrix Einträge laden
  getAll: async (): Promise<any[]> => {
    const response = await apiClient.get('/approval-matrix');
    return response.data;
  },

  // Neuen Eintrag erstellen
  create: async (department: string, approvers: Array<{ id: string; name: string; email: string }>): Promise<any> => {
    const response = await apiClient.post('/approval-matrix', {
      department,
      approvers
    });
    return response.data;
  },

  // Eintrag aktualisieren
  update: async (id: number, approvers: Array<{ id: string; name: string; email: string }>): Promise<any> => {
    const response = await apiClient.patch(`/approval-matrix/${id}`, {
      approvers
    });
    return response.data;
  },

  // Eintrag löschen
  delete: async (id: number): Promise<void> => {
    await apiClient.delete(`/approval-matrix/${id}`);
  },
};

// Statusverlauf API
export const statusverlaufApi = {
  getByBestellung: async (bestellungId: number): Promise<any[]> => {
    const response = await apiClient.get(`/bestellungen/${bestellungId}/verlauf`);
    return response.data;
  },
};

// Dokument-Typen API
export const dokumentTypenApi = {
  getAll: async (): Promise<any[]> => {
    const response = await apiClient.get('/dokument-typen');
    return response.data;
  },
  create: async (name: string, farbe: string, reihenfolge: number): Promise<any> => {
    const response = await apiClient.post('/dokument-typen', { name, farbe, reihenfolge });
    return response.data;
  },
  update: async (id: number, data: { name?: string; farbe?: string; reihenfolge?: number }): Promise<any> => {
    const response = await apiClient.put(`/dokument-typen/${id}`, data);
    return response.data;
  },
  delete: async (id: number): Promise<void> => {
    await apiClient.delete(`/dokument-typen/${id}`);
  },
};

// Dokumente API
export const dokumenteApi = {
  getByBestellung: async (bestellungId: number): Promise<any[]> => {
    const response = await apiClient.get(`/bestellungen/${bestellungId}/dokumente`);
    return response.data;
  },
  upload: async (
    bestellungId: number,
    file: File,
    opts?: { positionId?: number; dokumentTypId?: number }
  ): Promise<any> => {
    const form = new FormData();
    form.append('datei', file);
    if (opts?.positionId)  form.append('positionId',   String(opts.positionId));
    if (opts?.dokumentTypId) form.append('dokumentTypId', String(opts.dokumentTypId));
    const response = await apiClient.post(`/bestellungen/${bestellungId}/dokumente`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
  getDownloadUrl: (id: number): string => `/api/dokumente/${id}/download`,
  delete: async (id: number): Promise<void> => {
    await apiClient.delete(`/dokumente/${id}`);
  },
};
